import React, { useState } from 'react';
import { Kid, Reward } from '../types';
import { fireRewardConfetti } from '../utils/confetti';
import { playRewardRedeemFanfare } from '../utils/sound';

interface RedeemModalProps {
  isOpen: boolean;
  reward: Reward | null;
  kid: Kid | undefined;
  onClose: () => void;
  onRedeem: (rewardId: string, pin: string, kidId: string) => Promise<void>;
}

export const RedeemModal: React.FC<RedeemModalProps> = ({
  isOpen,
  reward,
  kid,
  onClose,
  onRedeem,
}) => {
  if (!isOpen || !reward) return null;

  const [pin, setPin] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleKeyPress = (digit: string) => {
    if (pin.length < 4) {
      setPin((prev) => prev + digit);
    }
  };

  const handleBackspace = () => {
    setPin((prev) => prev.slice(0, -1));
  };

  const handleConfirm = async (e: React.FormEvent) => {
    e.preventDefault();
    if (pin.length !== 4) {
      setError('Please enter your 4-digit Adult PIN');
      return;
    }

    try {
      setSubmitting(true);
      setError('');
      await onRedeem(reward.id, pin, kid?.id || '');
      fireRewardConfetti();
      playRewardRedeemFanfare();
      setPin('');
      onClose();
    } catch (err: any) {
      setError(err.message || 'Incorrect Adult PIN');
      setPin('');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-dialog"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        style={{ textAlign: 'center' }}
      >
        <div className="modal-header">
          <h3 className="modal-title" style={{ textAlign: 'left' }}>
            Adult PIN Required 🔒
          </h3>
          <button
            id="btn-close-redeem-modal"
            className="modal-close-btn"
            onClick={onClose}
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        <div style={{ margin: '16px 0 24px' }}>
          <div style={{ fontSize: '54px', marginBottom: '8px' }}>
            {reward.emoji}
          </div>
          <h4 style={{ fontSize: '20px', fontWeight: 800, color: '#1e1b4b' }}>
            Redeem {reward.name}?
          </h4>
          <p style={{ fontSize: '14px', color: '#64748b', marginTop: '4px' }}>
            This will use <strong>{reward.star_cost} ⭐</strong> {reward.is_shared === 1 ? 'shared stars' : `for ${kid?.name || 'this kid'}`}.
          </p>
          <p style={{ fontSize: '13px', color: '#8b5cf6', fontWeight: 700, marginTop: '8px' }}>
            Ask an adult to enter their 4-digit PIN below:
          </p>
        </div>

        {error && (
          <div
            style={{
              padding: '10px 14px',
              background: '#fee2e2',
              color: '#dc2626',
              borderRadius: '12px',
              fontSize: '13px',
              fontWeight: 700,
              marginBottom: '16px',
            }}
          >
            {error}
          </div>
        )}

        {/* 4 dots display */}
        <div className="pin-display">
          {[0, 1, 2, 3].map((idx) => (
            <div
              key={idx}
              className={`pin-dot ${pin.length > idx ? 'filled' : ''}`}
            />
          ))}
        </div>

        {/* Keypad */}
        <div className="pin-keypad">
          {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((digit) => (
            <button
              key={digit}
              type="button"
              id={`keypad-${digit}`}
              className="keypad-btn"
              onClick={() => handleKeyPress(digit)}
            >
              {digit}
            </button>
          ))}
          <button
            type="button"
            className="keypad-btn"
            onClick={() => setPin('')}
            style={{ fontSize: '14px', color: '#64748b' }}
          >
            Clear
          </button>
          <button
            type="button"
            id="keypad-0"
            className="keypad-btn"
            onClick={() => handleKeyPress('0')}
          >
            0
          </button>
          <button
            type="button"
            id="keypad-backspace"
            className="keypad-btn"
            onClick={handleBackspace}
          >
            ⌫
          </button>
        </div>

        <div style={{ marginTop: '24px' }}>
          <button
            id="btn-confirm-redeem"
            type="button"
            className="btn-primary-gradient"
            onClick={handleConfirm}
            disabled={submitting || pin.length !== 4}
            style={{
              opacity: pin.length === 4 ? 1 : 0.6,
              background: 'linear-gradient(135deg, #f59e0b, #ea580c)',
            }}
          >
            {submitting ? 'Confirming...' : 'Redeem Now! 🎁'}
          </button>
        </div>
      </div>
    </div>
  );
};
